/**
 * Запись модалки в history: системное «назад» закрывает модалку, а не уводит со страницы.
 *
 * Открытие помечает текущую запись именем модалки и кладёт поверх пустую, «назад» на помеченную закрывает модалку.
 * Закрыть из UI - шагом назад (`backFromModalHistoryEntry`): модалку закрывает её `onBack` после перехода,
 * URL к стору затем приводит `queryStringUpdater`. Новую модалку открывать после `waitModalHistoryBack`.
 */
interface IPendingBack {
  name: string;
  done: Promise<void>;
  resolve(): void;
}

/** Открытые модалки: имя → закрытие по «назад»; последняя - верхняя в истории */
const entries = new Map<string, () => void>();
/** Незавершённый шаг назад из `backFromModalHistoryEntry` */
let pendingBack: IPendingBack | null = null;
let isListening = false;

function createPendingBack(name: string): IPendingBack {
  let resolveDone: (() => void) | null = null;
  const done = new Promise<void>(resolve => {
    resolveDone = resolve;
  });

  return { name, done, resolve: () => resolveDone?.() };
}

function handlePopState(event: PopStateEvent): void {
  const ownBack = pendingBack;
  pendingBack = null;
  // Свой шаг назад закрывает ту, за кем ходили; системное «назад» - по метке записи
  const name: unknown = ownBack ? ownBack.name : event.state?.from;
  const onBack = typeof name === 'string' ? entries.get(name) : undefined;

  try {
    if (typeof name === 'string' && onBack) {
      entries.delete(name);
      onBack();
    }
  } finally {
    ownBack?.resolve();
  }
}

export function openModalHistoryEntry(name: string, onBack: () => void): void {
  if (!isListening) {
    window.addEventListener('popstate', handlePopState);
    isListening = true;
  }

  // Повторное открытие не копит обработчики и переносит модалку наверх
  entries.delete(name);
  window.history.replaceState({ ...window.history.state, from: name }, '');
  window.history.pushState({}, '');
  entries.set(name, onBack);
}

export function closeModalHistoryEntry(name: string): void {
  entries.delete(name);
}

/** Открыта ли запись модалки */
export function hasModalHistoryEntry(name: string): boolean {
  return entries.has(name);
}

/**
 * Закрыть модалку шагом назад; промис - после её `onBack`. Повторный вызов второй шаг не делает.
 * Модалка не верхняя - закрываем сразу без шага, чтобы не снять чужую запись.
 */
export function backFromModalHistoryEntry(name: string): Promise<void> {
  if (pendingBack?.name === name) {
    return pendingBack.done;
  }

  if (pendingBack) {
    return pendingBack.done.then(() => backFromModalHistoryEntry(name));
  }

  const onBack = entries.get(name);
  if (!onBack) {
    return Promise.resolve();
  }

  if (Array.from(entries.keys()).pop() !== name) {
    entries.delete(name);
    onBack();

    return Promise.resolve();
  }

  pendingBack = createPendingBack(name);
  const { done } = pendingBack;
  window.history.back();

  return done;
}

/** Дождаться незавершённого шага назад; его нет - сразу */
export function waitModalHistoryBack(): Promise<void> {
  return pendingBack?.done ?? Promise.resolve();
}
